import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Block } from "@mui/icons-material";

import Breadcrumbs from "../../../components/BreadCrumbs";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filter";
import messages from "../../../assets/locale/messages";
import { eventTypes, lookupsKeys } from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { getLookupsRequest } from "../../../store/Lookups/actions";
import emptyPlaceholder from "../../../assets/images/timesheet-screen/empty-report-placeholder.svg";
import {
  textContainOnlySpaces,
  renderTextEllipsis,
} from "../../../utils/Helpers";
import { ExcelIcon } from "../../../utils/Icons";
import { exportExcelResponse } from "../../../store/TimeSheet/actions";
import {
  UICard,
  UIButton,
  UIPaginationBar,
  UITable,
} from "../../../components/ui";
import SelectExportedColumnsModal from "./SelectExportedColumnsModal";
import "./TimeSheet.scss";
import {
  initialTimeSheetEventFetch,
  setTimeSheetEventFilters,
  setTimeSheetEventPage,
  setTimeSheetEventSearch,
} from "../../../store/PersistTimeSheetEvent/actions";

const getApplicantName = (row, lang) => {
  if (lang === "en" && row.user?.full_name_en?.trim()) {
    return row.user.full_name_en;
  }
  if (lang === "ar" && row.user?.full_name_ar?.trim()) {
    return row.user.full_name_ar;
  }
  return "-";
};

const DailyReport = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { eventid } = useParams();
  const linkRef = useRef(null);
  const location = useLocation();
  const { state } = location;

  const lang = useSelector((state) => state.locale.lang);
  const { timeSheet, shared, applicants } = messages[lang];
  const isLoading = useSelector((state) => state.loader);
  const rolesList = useSelector((state) => state.lookups.general.roles) || [];
  const list = useSelector((state) => state.timesheet.list);
  const meta = useSelector((state) => state.timesheet.meta);
  const excelLink = useSelector((state) => state.timesheet.excelLink);
  const persistStateEvent = useSelector((state) => state.persistTimeSheetEvent);

  const [q, setQuery] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [filterRemovedApplicant, setFilterRemovedApplicant] = useState(null);

  const pageSize = persistStateEvent.rowsCount || 10;
  const currentPage = meta?.currentPage || persistStateEvent.page || 1;
  const pageTitle = `${state?.event_name} - ${timeSheet.dailyReport.name}`;

  const getFilterValues = () =>
    persistStateEvent.filterRole
      .map((role) => rolesList.find((elt) => elt.id === role))
      .filter(Boolean)
      .map((role) => ({
        id: role.id,
        name: role.name,
      }));

  const removedApplicants = [
    { name: timeSheet.removed, label: timeSheet.removed, check: true },
    {
      name: timeSheet.assigned,
      label: timeSheet.assigned,
      check: false,
    },
  ];

  const handlePageChange = (newPage) => {
    dispatch(setTimeSheetEventPage(newPage));
  };

  const handlePageSizeChange = (nextSize) => {
    dispatch(
      setTimeSheetEventFilters({
        ...persistStateEvent,
        rowsCount: nextSize,
        page: 1,
      }),
    );
  };

  const handleFilterChange = (newValue) => {
    dispatch(setTimeSheetEventFilters({ ...persistStateEvent, ...newValue }));
  };

  const navigateToApplicantReport = useCallback(
    (row) => {
      history.push(
        ROUTE_PATHS.applicantReport
          .replace(":eventid", eventid)
          .replace(":userid", row.user.id),
        {
          isDaily: true,
          event_name: state?.event_name,
          filter_date: row.date,
          user_name: row.user,
        },
      );
    },
    [history, eventid, state?.event_name],
  );

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        ...row,
        id: `${row.user?.id}-${row.date}`,
      })),
    [list],
  );
 

  const columns = useMemo(
    () => [
      {
        field: "applicantName",
        headerName: timeSheet.applicantName,
        width: 200,
        flex: 0,
        sortable: false,
        valueGetter: (params) => getApplicantName(params.row, lang),
        renderCell: (params) => (
          <span className="timesheet-report-page__applicant-name">
            {renderTextEllipsis(params.value)}
            {params.row.user?.is_blocked && (
              <Block color="error" className="mx-2" fontSize="small" />
            )}
          </span>
        ),
      },
      {
        field: "role",
        headerName: timeSheet.role,
        width: 120,
        sortable: false,
        valueGetter: (params) =>
          lang === "ar"
            ? params.row?.role?.name_ar || params.row?.role?.name || "-"
            : params.row?.role?.name_en || params.row?.role?.name || "-",
      },
      {
        field: "number_of_days",
        headerName: timeSheet.numberOfWorkingDays,
        width: 140,
        sortable: false,
        renderCell: (params) => params.value || "-",
      },
      {
        field: "working_hours",
        headerName: timeSheet.dailyReport.workingHours,
        width: 160,
        sortable: false,
        renderCell: (params) => (
          <span dir="ltr" className={lang === "ar" ? "text-end d-block" : ""}>
            {params.value || "-"}
          </span>
        ),
      },
      {
        field: "training_number_of_days",
        headerName: timeSheet.trainingDays,
        width: 120,
        sortable: false,
        renderCell: (params) => params.value ?? "-",
      },
      {
        field: "training_expected_payroll",
        headerName: timeSheet.trainingPay,
        width: 130,
        sortable: false,
        renderCell: (params) =>
          params.value ? `${params.value} ${shared.sar}` : "-",
      },
      {
        field: "expected_payroll",
        headerName: timeSheet.dailyPayroll,
        width: 130,
        sortable: false,
        renderCell: (params) =>
          params.value ? `${params.value} ${shared.sar}` : "-",
      },
      {
        field: "total_expected_payroll",
        headerName: timeSheet.payroll,
        width: 140,
        sortable: false,
        renderCell: (params) =>
          params.value ? `${params.value} ${shared.sar}` : "-",
      },
    ],
    [timeSheet, shared, lang],
  );

  const paginationFrom = meta?.count > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, meta?.count || 0);
  const paginationPages = meta?.pages || 1;

  useEffect(() => {
    dispatch(getLookupsRequest([lookupsKeys.roles]));
  }, [lang, dispatch]);

  useEffect(() => {
    dispatch(initialTimeSheetEventFetch({ id: eventid }));
  }, [lang, persistStateEvent, eventid, dispatch]);

  useEffect(() => {
    if ((q.length >= 3 || q.length === 0) && !textContainOnlySpaces(q)) {
      dispatch(setTimeSheetEventSearch(q));
    }
  }, [q, dispatch]);

  useEffect(() => {
    if (excelLink) {
      linkRef.current.click();
      dispatch(exportExcelResponse(null));
    }
  }, [excelLink, dispatch]);

  const renderDailyReportsList = () => {
    if (!isLoading && !rows.length) {
      return (
        <UICard>
          <EmptyState
            description={timeSheet.placeholder}
            imgSrc={emptyPlaceholder}
            showActionButton={false}
          />
        </UICard>
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          hideFooter
          rowHeight={58}
          onRowClick={({ row }) => navigateToApplicantReport(row)}
          getRowClassName={() => "timesheet-report-page__row"}
        />
        {(meta?.count || 0) > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={applicants.listApplicants.showingRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={meta?.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="timesheet-report-page__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="timesheet-report-page events-list-container">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.timesheet,
              label: timeSheet.listTimeSheetBreadcrumb,
            },
            {
              path: "",
              label: pageTitle,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        {list?.length > 0 && (
          <Box className="d-flex justify-content-end align-items-center">
            <div className="filter-toggle-btn timesheet-report-page__export">
              <a
                ref={linkRef}
                href={excelLink}
                download={`${state?.event_name}-daily-report`}
                target="_blank"
                rel="noreferrer"
              />
              <UIButton
                type="button"
                variant="outlined"
                size="large"
                onClick={() => setOpenModal(true)}
              >
                <span className="d-flex align-items-center">
                  <ExcelIcon />
                  <span className="px-1 label">{timeSheet.exportBtn}</span>
                </span>
              </UIButton>
            </div>
          </Box>
        )}
      </div>

      <Filters
        isCollapsable
        searchInput={{
          placeholder: timeSheet.searchByApplicant,
          name: "search",
          value: q,
          onChange: (value) => setQuery(value),
        }}
        classNames={{
          search: "timesheet-report-page__search mb-3 mb-md-0",
        }}
        filters={[
          {
            type: "filter",
            name: "role",
            label: timeSheet.role,
            placeholder:
              persistStateEvent.filterRole.length === 0 ? timeSheet.role : "",
            value: getFilterValues(),
            options: rolesList?.map((option) => ({
              id: option.id,
              name: option.name,
            })),
            onChange: (value) =>
              handleFilterChange({ filterRole: value.map((role) => role.id) }),
            isMultiple: true,
            className: "multiple-role-filter-wrapper",
          },
          {
            type: "filter",
            name: "removedApplicants",
            placeholder: timeSheet.removedPlaceholder,
            value: filterRemovedApplicant,
            options: removedApplicants,
            onChange: (value) => setFilterRemovedApplicant(value),
          },
        ]}
      />

      <div className="timesheet-report-page__table-shell">
        {renderDailyReportsList()}
      </div>

      <SelectExportedColumnsModal
        removed={filterRemovedApplicant?.check}
        eventType={eventTypes.daily}
        open={openModal}
        setOpen={setOpenModal}
        eventName={state?.event_name}
      />
    </div>
  );
};

export default DailyReport;
